import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Star, Mail, MessageCircle, ArrowRight, Phone, Instagram, MapPin, Download } from "lucide-react";
import { MemoryLockGlyph } from "../components/MemoryLockGlyph";
import { MethodHeart } from "../components/MethodHeart";
import { SafetyBanner } from "../components/SafetyBanner";
import { isNativeAndroid, PLAY_STORE_URL } from "../lib/billing";

const HERO = "/hero-nebula.jpg";

const METHODS = [
  { to: "/lettura", testid: "method-sibille", image: "/methods/sibilla.jpg", title: "Sibille" },
  { to: "/iching", testid: "method-iching", image: "/methods/iching.jpg", title: "I Ching" },
  { to: "/geomanzia", testid: "method-geomanzia", image: "/methods/geomanzia.jpg", title: "Geomanzia" },
];

const Stars = () => (
  <>
    {[
      { top: "12%", left: "8%", s: 3, d: "0s" }, { top: "22%", left: "82%", s: 2, d: "0.8s" },
      { top: "8%", left: "55%", s: 2, d: "1.6s" }, { top: "35%", left: "20%", s: 2, d: "2.2s" },
      { top: "18%", left: "35%", s: 3, d: "1.1s" }, { top: "30%", left: "68%", s: 2, d: "2.8s" },
      { top: "5%", left: "30%", s: 2, d: "0.4s" }, { top: "15%", left: "91%", s: 1, d: "1.9s" },
      { top: "26%", left: "46%", s: 1, d: "2.5s" }, { top: "9%", left: "17%", s: 1, d: "3.1s" },
    ].map((st, i) => (
      <span key={i} className="star" style={{ top: st.top, left: st.left, width: st.s, height: st.s, animationDelay: st.d }} />
    ))}
    <span className="shooting-star" style={{ top: "14%", left: "76%" }} aria-hidden="true" data-testid="shooting-star" />
  </>
);

export default function Home() {
  const [reviews, setReviews] = useState([]);
  const [content, setContent] = useState(null);

  useEffect(() => {
    api.get("/reviews").then((r) => setReviews(r.data)).catch(() => {});
    api.get("/content").then((r) => setContent(r.data)).catch(() => {});
  }, []);

  const avg = reviews.length ? reviews.reduce((s, r) => s + r.rating, 0) / reviews.length : 0;

  return (
    <div data-testid="home-page">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${HERO})` }} />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(to bottom, #0f0617 0%, rgba(15,6,23,0.55) 15%, rgba(15,6,23,0.25) 38%, rgba(15,6,23,0.45) 60%, rgba(15,6,23,0.8) 80%, rgba(15,6,23,0.96) 92%, #0f0617 100%)",
          }}
        />
        <Stars />
        <div className="relative max-w-6xl mx-auto px-4 lg:px-8 pt-24 sm:pt-28 lg:pt-32 pb-20 sm:pb-28 text-center">
          <h1 className="mt-5 fade-up fade-up-1" data-testid="hero-title">
            <img src="/arcana-wordmark.png" alt="ARCANA" className="h-16 sm:h-20 lg:h-24 mx-auto" />
          </h1>
          <p className="font-cinzel tracking-[0.28em] uppercase text-amber-300/80 text-xs sm:text-sm mt-4 fade-up fade-up-1" data-testid="hero-tagline">
            {content?.home_tagline || "Antica cartomanzia & Oracoli"}
          </p>
          <p className="max-w-xl mx-auto mt-6 text-purple-100/80 text-base sm:text-lg leading-relaxed fade-up fade-up-2" data-testid="hero-subtitle">
            Letture con le carte, nate dallo studio della tradizione e da oltre trent'anni di esperienza nella cartomanzia.
          </p>
          <div className="fade-up fade-up-3 grid grid-cols-2 gap-3 sm:gap-5 max-w-xl mx-auto mt-8" data-testid="hero-info-boxes">
            <Link
              to="/come-funziona"
              data-testid="come-funziona-link"
              className="group rounded-2xl border border-[#4a2175] bg-[#180a26]/80 px-4 py-5 sm:py-6 hover:border-amber-500/60 hover:-translate-y-1 transition-all duration-300 shadow-[0_0_40px_rgba(74,33,117,0.35)]"
            >
              <h3 className="font-display text-sm sm:text-lg leading-tight text-amber-100">Come funziona Arcana</h3>
              <div className="flex items-center justify-center gap-2 mt-3">
                <span className="h-px w-6 bg-amber-500/40" />
                <span className="w-1 h-1 rotate-45 bg-amber-400/70" />
                <span className="h-px w-6 bg-amber-500/40" />
              </div>
            </Link>
            <Link
              to="/oltre-le-carte"
              data-testid="oltre-le-carte-link"
              className="group rounded-2xl border border-[#4a2175] bg-[#180a26]/80 px-4 py-5 sm:py-6 hover:border-amber-500/60 hover:-translate-y-1 transition-all duration-300 shadow-[0_0_40px_rgba(74,33,117,0.35)]"
            >
              <h3 className="font-display text-sm sm:text-lg leading-tight text-amber-100">Oltre le carte</h3>
              <div className="flex items-center justify-center gap-2 mt-3">
                <span className="h-px w-6 bg-amber-500/40" />
                <span className="w-1 h-1 rotate-45 bg-amber-400/70" />
                <span className="h-px w-6 bg-amber-500/40" />
              </div>
            </Link>
          </div>
          {!isNativeAndroid() && (
            <div className="fade-up fade-up-3 mt-8" data-testid="scarica-app-hero">
              <a
                href={PLAY_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="scarica-app-btn"
                className="gold-btn inline-flex items-center gap-2 px-8 py-3.5 text-sm tracking-[0.12em] uppercase"
              >
                <Download className="w-4 h-4" /> Scarica l'app
              </a>
            </div>
          )}
        </div>
      </section>
      {content?.promo_banner ? (
        <section className="px-4 pt-8" data-testid="promo-banner">
          <div className="max-w-2xl mx-auto rounded-2xl border border-amber-500/40 bg-amber-500/10 px-6 py-4 text-center">
            <p className="text-amber-100 text-sm leading-relaxed">{content.promo_banner}</p>
          </div>
        </section>
      ) : null}

      <SafetyBanner />

      <section id="metodi" className="max-w-5xl mx-auto px-4 lg:px-8 pt-12 sm:pt-16 pb-16 sm:pb-20" data-testid="home-methods-section">
        <div className="text-center mb-10">
          <p className="eyebrow">I Metodi</p>
          <h2 className="font-display text-3xl sm:text-4xl text-amber-100 mt-3">Scegli la tua lettura</h2>
          {content?.home_methods_intro ? (
            <p className="text-purple-200/60 text-sm mt-4 max-w-xl mx-auto leading-relaxed" data-testid="home-methods-intro">{content.home_methods_intro}</p>
          ) : null}
        </div>
        <div className="grid grid-cols-3 gap-3 sm:gap-6" data-testid="home-methods-grid">
          {METHODS.map((m) => (
            <Link
              key={m.testid}
              to={m.to}
              data-testid={m.testid}
              className="group relative rounded-2xl border border-[#4a2175] bg-[#180a26] overflow-hidden hover:border-amber-500/60 hover:-translate-y-1 transition-all duration-300 shadow-[0_0_40px_rgba(74,33,117,0.35)]"
            >
              <div className="aspect-square overflow-hidden bg-[#12081e] flex items-center justify-center">
                <img
                  src={m.image}
                  alt={m.title}
                  loading="lazy"
                  className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="py-2.5 px-2 sm:py-4 sm:px-3 text-center border-t border-amber-500/20">
                <div className="flex items-center justify-center gap-1.5">
                  <h3 className="font-display text-[13px] sm:text-xl leading-tight text-amber-100">{m.title}</h3>
                  <MethodHeart metodo={m.to.slice(1)} testid={`${m.testid}-heart`} inline />
                </div>
                <div className="flex items-center justify-center gap-2 mt-2">
                  <span className="h-px w-6 bg-amber-500/40" />
                  <span className="w-1 h-1 rotate-45 bg-amber-400/70" />
                  <span className="h-px w-6 bg-amber-500/40" />
                </div>
              </div>
            </Link>
          ))}
        </div>

        <div className="text-center mt-10" data-testid="home-discover-section">
          <Link
            to="/scopri-altro"
            data-testid="discover-more-link"
            className="group inline-flex items-center gap-3 text-amber-300/90 hover:text-amber-200 text-xs sm:text-sm tracking-[0.25em] uppercase transition-colors"
          >
            <span className="h-px w-8 bg-amber-500/40 group-hover:w-11 transition-all duration-300" />
            <span className="inline-flex items-center gap-2 border-b border-amber-500/40 group-hover:border-amber-300/80 pb-1 transition-colors">
              Altri metodi
              <ArrowRight className="w-3.5 h-3.5 opacity-70 group-hover:translate-x-1 transition-transform duration-300" />
            </span>
            <span className="h-px w-8 bg-amber-500/40 group-hover:w-11 transition-all duration-300" />
          </Link>
        </div>

        <Link
          to="/memoria"
          data-testid="home-memoria-tile"
          className="chrome-preview-card mt-10 mx-auto max-w-md flex flex-col items-center text-center px-6 py-7 hover:border-amber-400/70 hover:-translate-y-0.5 transition-all duration-300"
        >
          <div className="w-14 h-14 rounded-full border border-amber-500/40 bg-[#1a0b2b] flex items-center justify-center shadow-[0_0_24px_rgba(212,175,55,0.25)]">
            <MemoryLockGlyph className="w-9 h-9" />
          </div>
          <p className="font-display text-amber-100 text-lg mt-4">La mia memoria</p>
          <p className="text-purple-200/70 text-xs mt-1.5 leading-relaxed">
            Tutto ciò che fai in questo spazio resta chiuso a chiave. Puoi fare letture private, approfondire, scrivere riflessioni e riprendere i dialoghi quando vuoi. Nessun altro può vedere quello che succede qui.
          </p>
          <span className="mt-4 inline-flex items-center text-[11px] uppercase tracking-[0.2em] text-amber-300/90 border border-amber-500/40 rounded-full px-4 py-1.5 group-hover:bg-amber-500/10 transition-colors">
            Apri la tua memoria
          </span>
        </Link>

        <div className="max-w-2xl mx-auto text-center mt-12 sm:mt-14" data-testid="home-method-note">
          <div className="flex items-center justify-center gap-3 mb-5">
            <span className="h-px w-10 bg-amber-500/40" />
            <span className="w-1.5 h-1.5 rotate-45 bg-amber-400/70" />
            <span className="h-px w-10 bg-amber-500/40" />
          </div>
          <p className="text-purple-100/80 text-sm sm:text-base leading-relaxed italic">
            “Non è un’intelligenza artificiale qualsiasi. Dietro c’è il mio modo di leggere le carte, costruito con anni e anni di studio sulle Sibille, sui loro significati, sulle loro sfumature, sugli accostamenti tra le carte e su tutto ciò che emerge da una stesa.”
          </p>
        </div>
      </section>

      <section className="max-w-2xl mx-auto px-4 lg:px-8 pb-16 sm:pb-20 text-center" data-testid="home-booking-section">
        <h2 className="font-display text-3xl sm:text-4xl text-amber-100">Il consulto personale con Virginia</h2>
        <p className="text-purple-200/70 text-sm mt-4 max-w-xl mx-auto leading-relaxed">
          Le letture dell'applicazione sono uno strumento di spunti e riflessione in autonomia. Per informazioni o un'analisi
          davvero professionale della tua situazione, prenota un consulto diretto con Virginia.
        </p>
        <div className="mt-7 flex flex-wrap items-center justify-center gap-4">
          {content?.contact_whatsapp && (
            <a
              href={`https://wa.me/${content.contact_whatsapp.replace(/\D/g, "")}`}
              target="_blank"
              rel="noreferrer"
              data-testid="booking-whatsapp-btn"
              className="gold-btn inline-flex items-center gap-2 px-7 py-3 text-sm"
            >
              <MessageCircle className="w-4 h-4" /> Scrivimi su WhatsApp
            </a>
          )}
          {content?.contact_email && (
            <a
              href={`mailto:${content.contact_email}`}
              data-testid="booking-email-btn"
              className="inline-flex items-center gap-2 px-7 py-3 text-sm rounded-full border border-amber-500/40 text-amber-200 hover:bg-amber-500/10 transition-all"
            >
              <Mail className="w-4 h-4" /> Scrivimi per email
            </a>
          )}
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-4 lg:px-8 pb-16 sm:pb-20" data-testid="home-reviews-section">
        <h2 className="font-display text-3xl sm:text-4xl text-amber-100 text-center mb-10">Recensioni</h2>
        {reviews.length === 0 ? (
          <div className="text-center max-w-xl mx-auto" data-testid="reviews-empty-state">
            <Star className="w-7 h-7 text-amber-400/60 mx-auto mb-4" />
            <p className="text-purple-200/70 text-sm">Le recensioni verranno pubblicate qui.</p>
          </div>
        ) : (
          <div className="max-w-xl mx-auto text-center" data-testid="reviews-box">
            <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-2" data-testid="reviews-summary">
              <div className="flex gap-0.5">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} className={`w-4 h-4 ${i < Math.round(avg) ? "text-amber-400 fill-amber-400" : "text-purple-300/25"}`} />
                ))}
              </div>
              <span className="text-amber-200 font-medium text-sm" data-testid="reviews-average">{avg.toFixed(1)}</span>
              <span className="text-purple-300/60 text-xs" data-testid="reviews-count">· {reviews.length} {reviews.length === 1 ? "recensione" : "recensioni"}</span>
            </div>
            <div className="mt-9 space-y-8">
              {reviews.slice(0, 2).map((r, idx) => (
                <div key={r.id}>
                  {idx > 0 && <div className="h-px w-16 bg-amber-500/25 mx-auto mb-8" />}
                  <div className="flex gap-0.5 justify-center mb-2.5">
                    {Array.from({ length: r.rating }).map((_, i) => (
                      <Star key={i} className="w-3.5 h-3.5 text-amber-400 fill-amber-400" />
                    ))}
                  </div>
                  <p className="text-purple-100/80 text-sm leading-relaxed italic" data-testid={idx === 0 ? "reviews-preview-text" : undefined}>
                    “{r.text.length > 140 ? r.text.slice(0, 140).trimEnd() + "…" : r.text}”
                  </p>
                  <p className="text-amber-300 text-xs mt-2.5 font-medium">{r.author}</p>
                  {r.reply && (
                    <p className="text-purple-200/60 text-xs mt-2" data-testid={`reviews-preview-reply-${idx}`}>
                      <span className="text-amber-300/80 font-medium">Risposta di Virginia:</span> {r.reply.length > 90 ? r.reply.slice(0, 90).trimEnd() + "…" : r.reply}
                    </p>
                  )}
                </div>
              ))}
            </div>
            <Link
              to="/recensioni"
              data-testid="reviews-see-all"
              className="inline-block mt-8 text-xs text-amber-300/90 tracking-wide underline underline-offset-4 decoration-amber-500/40 hover:text-amber-200 transition-colors"
            >
              Leggi tutte le recensioni
            </Link>
          </div>
        )}
      </section>
    </div>
  );
}
